
import { defineComponent, inject } from 'vue'
import TextInput from '@/components/shared/TextInput.vue'
import LoadingPulse from '@/components/shared/LoadingPulse.vue'
import Paginator from '@/components/shared/Paginator.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import { CaliberService } from '@/services/caliber.service'
import { Caliber } from '@/@types/responses/caliber/Caliber'
import Toaster from '@meforma/vue-toaster'
import TrashCanSvg from '@/components/svgs/TrashCanSvg.vue'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'

export default defineComponent({
  name: 'Calibers',
  components: {
    LoadingPulse,
    Paginator,
    TextInput,
    TrashCanSvg
  },
  setup() {
    const caliberService = inject(PROVIDER_CONSTANTS.caliberService) as CaliberService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

    return {
      caliberService,
      toast
    }
  },
  data() {
    return {
      ascending: true,
      calibers: [] as Caliber[],
      filterOptionsExpanded: true,
      loading: true,
      pageNumber: 1,
      pageSize: 10,
      searchTerm: '',
      sortField: 'Name',
      totalResults: 0
    }
  },
  mounted() {
    window.addEventListener("popstate", this.parseQueryStringAndSearch)

    this.parseQueryStringAndSearch()
  },
  unmounted() {
    window.removeEventListener('popstate', this.parseQueryStringAndSearch)
  },
  methods: {
    clearSearchForm(): void{
      this.searchTerm = ''
      this.search()
    },
    getBooleanParameterByName(name: string): boolean | undefined {
      const parameter = this.getStringParameterByName(name)
      return parameter !== undefined ? parameter === 'true' : undefined
    },
    getNumericParameterByName(name: string): number | undefined {
      const parameter = this.getStringParameterByName(name)
      return parameter !== undefined ? Number(parameter) : undefined
    },
    getStringParameterByName(name: string): string | undefined {
      const regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)")
      const results = regex.exec(window.location.href)

      if (!results || !results[2])
        return undefined
      
      return decodeURIComponent(results[2].replace(/\+/g, " "));
    },
    onDeleteClicked(id: number): void {
      this.caliberService.deleteCaliber(id).then(() => {
        if ((this.calibers.length - 1) === 0 && this.pageNumber > 1)
          this.onPageChanged(this.pageNumber - 1)
        else
          this.searchCalibers()

        this.toast.success('Caliber deleted successfully.')
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to delete the caliber. Please try again.')
        }
      })
    },
    onPageChanged(pageNumber: number): void {
      this.pageNumber = pageNumber
      this.searchCalibers()
    },
    onPageSizeChanged(pageSize: number): void {
      this.pageSize = pageSize
      this.search()
    },
    onTableRowClicked(id: number): void {
      this.$router.push('/caliber/' + id)
    },
    parseQueryStringAndSearch(): void {
      const ascendingFromUrl = this.getBooleanParameterByName("a")
      this.ascending = ascendingFromUrl !== undefined ? ascendingFromUrl : this.ascending

      const pageNumberFromUrl = this.getNumericParameterByName("pn")
      this.pageNumber = pageNumberFromUrl !== undefined ? pageNumberFromUrl : this.pageNumber

      const pageSizeFromUrl = this.getNumericParameterByName("ps")
      this.pageSize = pageSizeFromUrl !== undefined ? pageSizeFromUrl : this.pageSize

      const searchTermFromUrl = this.getStringParameterByName("st")
      this.searchTerm = searchTermFromUrl !== undefined ? searchTermFromUrl : this.searchTerm

      const sortFieldFromUrl = this.getStringParameterByName("sf")
      this.sortField = sortFieldFromUrl !== undefined ? sortFieldFromUrl : this.sortField

      this.searchCalibers()
    },
    search(): void {
      this.pageNumber = 1
      this.searchCalibers()
    },
    searchCalibers(): void {
      this.loading = true
      this.updateUrl()
      this.caliberService.searchCalibers(this.searchTerm, this.pageNumber, this.pageSize, this.sortField, this.ascending).then(pagingResponse => {
        this.totalResults = pagingResponse.totalResults
        this.calibers = pagingResponse.results
        this.loading = false
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the calibers. Try reloading the page.')
        }
      })
    },
    sortBy(sortField: string): void {
      if (this.sortField === sortField)
        this.ascending = !this.ascending
      else
        this.sortField = sortField

      this.searchCalibers()
    },
    updateUrl(): void {
      let newQueryString = `?a=${this.ascending}&pn=${this.pageNumber}&ps=${this.pageSize}&sf=${this.sortField}`

      if (this.searchTerm !== '')
        newQueryString = newQueryString + `&st=${encodeURIComponent(this.searchTerm)}`
      
      this.$router.replace('/calibers' + newQueryString)
    }
  }
})
